import { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ForumIcon from '@mui/icons-material/Forum';
import colors from './../../../src/theme/colors.js';
import {
  Box,
  Drawer,
  Typography,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import { useAuth } from "../../hooks/use-auth";
import { useNavigate, useLocation } from "react-router-dom";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ListIcon from "@mui/icons-material/List";
import TroubleshootOutlinedIcon from "@mui/icons-material/TroubleshootOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { usePermissionRedirect } from "./usePermissionRedirect";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import usePermissionCheck from "./usePermissionCheck";
import REACT_APP_LOGO_NAVBAR from './../../images/Logo_Kalanke_Blanc.png';

// Couleur principale sanctuarisée
const PRIMARY = "#6060B8";
const SIDEBAR_BG = "#8282CA";

// Liste unique de tous les composants à vérifier
const ALL_PERMISSION_COMPONENTS = [
  "Scores", "Apprenants", "Forum", "Actions", "EPP", "Satisfaction",
  "Liste des sessions", "Agenda", "Experts", "Administrateurs",
  "Activités", "Paramètres", "Rôles",
];

export const DashboardSidebar = (props) => {
  const { onClose, open, anchorEl, openSide, setOpenSide, ...other } = props;
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"), {
    noSsr: true,
  });

  usePermissionCheck();
  usePermissionRedirect(ALL_PERMISSION_COMPONENTS);

  const tenant = useMemo(() => globalThis.localStorage.getItem('tenant'), []);

  const hasPermission = useCallback(
    (component, requiredPermission) =>
      user.components_and_permissions.some(
        (perm) => perm.component === component && perm.permission === requiredPermission
      ),
    [user.components_and_permissions]
  );

  const sections = useMemo(() => {
    if (user?.id_service === 0) {
      return [
        {
          title: null,
          items: [
            {
              icon: <HomeIcon fontSize="small" />,
              title: "Tableau de bord",
              path: `/${tenant}/dashboard`,
            },
          ],
        },
        {
          title: "Analyses",
          icon: <TroubleshootOutlinedIcon fontSize="small" />,
          items: [
            hasPermission("Scores", "Voir") && { title: "Scores", path: `/${tenant}/dashboard/scores` },
            hasPermission("Performances", "Voir") && { title: "Performances" },
          ].filter(Boolean),
        },
        {
          title: "Apprenants",
          icon: <GroupOutlinedIcon fontSize="small" />,
          items: [
            hasPermission("Apprenants", "Voir") && { title: "Liste des apprenants", path: `/${tenant}/dashboard/customers` },
            hasPermission("Apprenants", "Créer") && { title: "Créer un apprenant", path: `/${tenant}/dashboard/create/customers` },
            hasPermission("Apprenants", "Créer") && { title: "Groupes", path: `/${tenant}/dashboard/group` },
            hasPermission("Forum", "Voir") && { title: "Forum", path: `/${tenant}/dashboard/forum` },
          ].filter(Boolean),
        },
        {
          title: "Conception",
          icon: <AssessmentOutlinedIcon fontSize="small" />,
          items: [
            (hasPermission("Actions", "Voir") || hasPermission("Actions", "Créer")) && { title: "Actions", path: `/${tenant}/dashboard/actions` },
            (hasPermission("EPP", "Voir") || hasPermission("EPP", "Créer")) && { title: "Audits", path: `/${tenant}/dashboard/epp/audits` },
            (hasPermission("EPP", "Voir") || hasPermission("EPP", "Créer")) && { title: "TCS", path: `/${tenant}/dashboard/epp/tcs` },
            hasPermission("Dossier patient", "Voir") && { title: "Dossiers patients", path: `/${tenant}/dashboard/patients_folder` },
            hasPermission("Gestion des Risques", "Voir") && { title: "Gestion des Risques" },
          ].filter(Boolean),
        },
        {
          title: "Qualité",
          icon: <VerifiedOutlinedIcon fontSize="small" />,
          items: [
            hasPermission("Satisfaction", "Voir") && { title: "Satisfaction", path: `/${tenant}/dashboard/satisfaction` },
            (hasPermission("EPP", "Voir") || hasPermission("EPP", "Créer")) && { title: "Questionnaires Formation", path: `/${tenant}/dashboard/formation-questionnaire` },
          ].filter(Boolean),
        },
        {
          title: "Sessions",
          icon: <Groups2OutlinedIcon fontSize="small" />,
          items: [
            (hasPermission("Sessions", "Voir") || hasPermission("Sessions", "Créer")) && { title: "Liste des sessions", path: `/${tenant}/dashboard/session` },
            hasPermission("Agenda", "Voir") && { title: "Agenda", path: `/${tenant}/dashboard/agenda` },
          ].filter(Boolean),
        },
        {
          title: "Experts",
          icon: <WorkOutlineIcon fontSize="small" />,
          items: [
            hasPermission("Experts", "Voir") && { title: "Liste des experts", path: `/${tenant}/dashboard/experts` },
            { title: "Comité scientifique" },
          ].filter(Boolean),
        },
        {
          title: "Administration",
          icon: <AdminPanelSettingsIcon fontSize="small" />,
          items: [
            hasPermission("Administrateurs", "Voir") && { title: "Liste des administrateurs", path: `/${tenant}/dashboard/admins` },
            hasPermission("Activités", "Voir") && { title: "Activités", path: `/${tenant}/dashboard/activities` },
            hasPermission("Administrateurs", "Créer") && { title: "Créer un admin", path: `/${tenant}/dashboard/create/admin` },
          ].filter(Boolean),
        },
        {
          title: "Règlages",
          icon: <SettingsIcon fontSize="small" />,
          items: [
            hasPermission("Paramètres", "Voir") && { title: "Paramètres", path: `/${tenant}/dashboard/parametres` },
            hasPermission("Apprenants", "Créer") && { title: "Import", path: `/${tenant}/dashboard/import` },
            hasPermission("Rôles", "Voir") && { title: "Rôles", path: `/${tenant}/dashboard/roles` },
          ].filter(Boolean),
        },
      ].filter((section) => section.items.length > 0);
    }

    if (user?.id_service === 1) {
      return [
        {
          title: "Analyses",
          icon: <HomeIcon fontSize="small" />,
          items: [
            { title: "Tableau de bord", path: `/${tenant}/dashboard` },
            { title: "Agenda", path: `/${tenant}/dashboard/agenda` },
          ],
        },
        {
          title: "Évaluations",
          icon: <VerifiedOutlinedIcon fontSize="small" />,
          items: [
            { title: "Audits", path: `/${tenant}/dashboard/audits` },
            { title: "TCS", path: `/${tenant}/dashboard/tcs` },
            { title: "Apprenants", path: `/${tenant}/dashboard/customers` },
          ],
        },
        {
          title: "Sessions",
          icon: <ListIcon fontSize="small" />,
          items: [{ title: "Liste des sessions", path: `/${tenant}/dashboard/session` }],
        },
        {
          title: "Autres",
          icon: <ForumIcon fontSize="small" />,
          items: [{ title: "Forum", path: `/${tenant}/dashboard/forum` }],
        },
      ].filter((section) => section.items.length > 0);
    }

    return [];
  }, [user, tenant, hasPermission]);

  const handleOpen = useCallback(() => setOpenSide(true), [setOpenSide]);
  const handleClose = useCallback(() => setOpenSide(false), [setOpenSide]);

  const isItemActive = useCallback(
    (item) => {
      if (!item.path) return false;
      const path = location.pathname;
      return path === item.path || path.startsWith(item.path + "/");
    },
    [location.pathname]
  );

  const handleNavigate = useCallback(
    (path) => { if (path) navigate(path); },
    [navigate]
  );

  const content = useMemo(
    () => (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        {/* ── Zone Logo & Tenant ── */}
        <Box
          sx={{
            px: 3, pt: 3, pb: 2.5, flexShrink: 0,
            borderBottom: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <Box
            component="img"
            sx={{ width: "140px", height: "auto", display: "block" }}
            alt="Kalanke"
            src={REACT_APP_LOGO_NAVBAR}
          />
          {tenant && (
            <Box
              sx={{
                display: "inline-flex", alignItems: "center", mt: 1.5,
                px: 1.5, py: 0.4, backgroundColor: "rgba(255,255,255,0.18)", borderRadius: "6px",
              }}
            >
              <Typography sx={{ color: "rgba(255,255,255,0.95)", fontSize: "10px", fontWeight: 700, letterSpacing: "1px" }}>
                {tenant.toUpperCase()}
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── Navigation ── */}
        <Box
          sx={{
            flexGrow: 1, overflowY: "auto", px: 1.5, py: 1.5,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {sections.map((section) => {
            const isNullSection = section.title === null;

            // ── Lien standalone (ex: Tableau de bord) — correspondance exacte uniquement ──
            if (isNullSection) {
              return section.items.map((item) => {
                const isActive = item.path ? location.pathname === item.path : false;
                return (
                  <Box
                    key={String(item.title)}
                    onClick={() => handleNavigate(item.path)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.5,
                      px: 1.5, py: 1.125, mb: 0.5, borderRadius: "10px",
                      cursor: item.path ? "pointer" : "default",
                      backgroundColor: isActive ? "rgba(255,255,255,0.22)" : "transparent",
                      borderLeft: isActive ? "3px solid rgba(255,255,255,0.9)" : "3px solid transparent",
                      transition: "background-color 0.2s ease, border-left 0.2s ease",
                      "&:hover": item.path ? { backgroundColor: "rgba(255,255,255,0.15)" } : {},
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", color: "rgba(255,255,255,0.9)", flexShrink: 0 }}>
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontSize: "13px", fontWeight: isActive ? 700 : 500, color: "white" }}>
                      {item.title}
                    </Typography>
                  </Box>
                );
              });
            }

            // ── Section toujours dépliée ──
            const hasSectionActive = section.items.some((item) => isItemActive(item));
            return (
              <Box key={section.title} sx={{ mb: 1 }}>
                {/* En-tête de section — label non cliquable */}
                <Box
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    px: 1.5, py: 0.75,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", color: hasSectionActive ? "rgba(255,255,255,1)" : "rgba(255,255,255,0.55)", flexShrink: 0 }}>
                    {section.icon}
                  </Box>
                  <Typography
                    sx={{
                      flex: 1, fontSize: "11px", fontWeight: 700,
                      color: hasSectionActive ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.55)",
                      textTransform: "uppercase", letterSpacing: "0.08em",
                    }}
                  >
                    {section.title}
                  </Typography>
                </Box>

                {/* Items toujours visibles */}
                <Box sx={{ pb: 0.5, pl: 0.5 }}>
                  {section.items.map((item) => {
                    const isActive = isItemActive(item);
                    return (
                      <Box
                        key={item.title}
                        onClick={() => handleNavigate(item.path)}
                        sx={{
                          display: "flex", alignItems: "center",
                          pl: 4, pr: 1.5, py: 0.75, mb: 0.25,
                          borderRadius: "8px",
                          cursor: item.path ? "pointer" : "default",
                          opacity: item.path ? 1 : 0.45,
                          backgroundColor: isActive ? "rgba(255,255,255,0.18)" : "transparent",
                          borderLeft: isActive ? "3px solid rgba(255,255,255,0.85)" : "3px solid transparent",
                          transition: "all 0.15s ease",
                          "&:hover": item.path ? { backgroundColor: "rgba(255,255,255,0.1)" } : {},
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12.5px",
                            fontWeight: isActive ? 700 : 400,
                            color: isActive ? "white" : "rgba(255,255,255,0.82)",
                            lineHeight: 1.5,
                          }}
                        >
                          {item.title}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    ),
    [sections, tenant, location.pathname, isItemActive, handleNavigate]
  );

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open
        PaperProps={{
          sx: {
            backgroundColor: SIDEBAR_BG,
            borderRight: "none",
            color: "white",
            width: 240,
            boxShadow: "2px 0 12px rgba(0,0,0,0.15)",
          },
        }}
        variant="persistent"
      >
        {content}
      </Drawer>
    );
  }

  return (
    <>
      <Drawer
        anchor="left"
        open={openSide}
        onClose={handleClose}
        PaperProps={{
          sx: {
            backgroundColor: SIDEBAR_BG,
            borderRight: "none",
            color: "white",
            width: 240,
            boxShadow: "4px 0 16px rgba(0,0,0,0.2)",
            top: "64px",
            height: "calc(100% - 64px)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            px: 1,
            pt: 0.75,
            pb: 0,
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.75)",
              "&:hover": {
                color: "white",
                backgroundColor: "rgba(255,255,255,0.15)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {content}
      </Drawer>
      <ToastContainer />
    </>
  );
};

DashboardSidebar.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  anchorEl: PropTypes.any,
  openSide: PropTypes.bool,
  setOpenSide: PropTypes.func,
};

export default DashboardSidebar;
